import { type AgentRun } from '@lucidtech/las-sdk-browser';
import { AccessorKeyColumnDef } from '@tanstack/react-table';
import { memo, useEffect, useMemo, useRef } from 'react';
import { DialogTrigger } from 'react-aria-components';

import { Button, Heading, Loading, Text } from '@/components/core';
import { AgentRunDocumentCard, AgentRunStatus, InviteMembersModal, Profile } from '@/components/ui';
import { ManagedRole } from '@/constants';
import { SampleDocumentTooltip } from '@/features/agents';
import { useGetAgentFromPath, useListReviewers, useMeasuredSize } from '@/hooks';
import { toModelId, useGetModel } from '@/hooks/api';
import { InboxProvider } from '@/store';
import { findModelId, isNumber } from '@/utils';

import { AgentRunEventsCell } from './AgentRunEventsCell';
import { AgentRunsTable } from './AgentRunsTable';
import { ConfigureModelCTA } from './ConfigureModelCTA.tsx';
import { Elapsed } from './Elapsed.tsx';
import { InboxSelectedToolbar } from './InboxSelectedToolbar';
import { RunOverflowMenu } from './RunOverflowMenu.tsx';
import { RunsActionBar } from './RunsActionBar.tsx';
import { SetupAutomaticExportBanner } from './SetupAutomaticExportBanner.tsx';

const INBOX_WIDTH_KEY = 'cradl:inboxWidth';

export type InboxProps = object;

// eslint-disable-next-line no-empty-pattern
const IInbox = ({}: InboxProps) => {
  const widthRef = useRef<HTMLDivElement | null>(null);
  const { width: tableWidth } = useMeasuredSize(widthRef, {
    width: () => {
      const storedInboxWidth = localStorage.getItem(INBOX_WIDTH_KEY);
      const inboxWidth = storedInboxWidth ? Number(storedInboxWidth) : undefined;
      return isNumber(inboxWidth) ? inboxWidth : undefined;
    },
  });

  useEffect(() => {
    if (isNumber(tableWidth)) {
      localStorage.setItem(INBOX_WIDTH_KEY, tableWidth.toString());
    }
  }, [tableWidth]);

  const statusSize = 200;
  const createdTimeSize = 150;
  const overflowMenuSize = 40;

  const documentSize = useMemo(() => {
    if (!isNumber(tableWidth)) return 300;
    return tableWidth / 3;
  }, [tableWidth]);

  const eventsSize = useMemo(() => {
    if (!isNumber(tableWidth)) return 300;
    return tableWidth / 5;
  }, [tableWidth]);

  const columns = useMemo<AccessorKeyColumnDef<AgentRun>[]>(() => {
    return [
      {
        accessorKey: 'file',
        header: () => (
          <Heading className="text-gray-500" size="xs">
            File
          </Heading>
        ),
        cell: ({ row }) => <AgentRunDocumentCard agentId={row.original.agentId} runId={row.original.runId} />,
        minSize: documentSize,
        maxSize: Number.MAX_SAFE_INTEGER,
        enableSorting: false,
      },
      {
        accessorKey: 'status',
        header: () => (
          <Heading className="text-gray-500" size="xs">
            Status
          </Heading>
        ),
        cell: ({ row }) => <AgentRunStatus agentId={row.original.agentId} runId={row.original.runId} />,
        enableSorting: false,
        size: statusSize,
      },
      {
        accessorKey: 'createdTime',
        header: () => (
          <Heading className="text-gray-500" size="xs">
            Started
          </Heading>
        ),
        cell: ({ row }) => <Elapsed agentId={row.original.agentId} runId={row.original.runId} />,
        size: createdTimeSize,
      },
      {
        accessorKey: 'events',
        header: () => (
          <Heading className="text-gray-500" size="xs">
            Events
          </Heading>
        ),
        cell: ({ row }) => <AgentRunEventsCell agentId={row.original.agentId} runId={row.original.runId} />,
        minSize: eventsSize,
        maxSize: Number.MAX_SAFE_INTEGER,
        enableSorting: false,
      },
      {
        accessorKey: 'other',
        header: () => null,
        cell: ({ row }) => <RunOverflowMenu agentId={row.original.agentId} runId={row.original.runId} />,
        enableSorting: false,
        size: overflowMenuSize,
      },
    ];
  }, [documentSize, eventsSize]);

  const { data: agent } = useGetAgentFromPath();
  const { data: model, isFetched: modelIsFetched } = useGetModel(toModelId(findModelId(agent)));
  const { users } = useListReviewers({ agentId: agent?.agentId });

  const isModelConfigured = useMemo(() => {
    return Object.entries(model?.fieldConfig ?? {}).length;
  }, [model]);

  if (!agent) {
    return <Loading label="Loading runs ..." />;
  }

  return (
    <div className="flex min-h-0 w-full flex-col">
      <div className="py-md gap-sm flex h-12 w-full items-center border-b-1 border-gray-200 bg-white px-8">
        <div className="gap-sm flex items-center">
          <Heading className="font-medium text-gray-500" size="sm">
            Runs
          </Heading>
          <Text className="font-bold text-gray-200">/</Text>
          <Heading className="font-medium text-gray-600" size="sm">
            Overview
          </Heading>
        </div>
        <div className="grow" />
        <div className="flex items-center">
          {users?.map((user) => (
            <Profile
              className="-mx-0.5 bg-white"
              profileId={user.profileId}
              showEmail={false}
              showName={false}
              props={{
                img: {
                  className:
                    'size-6 bg-white outline-1 outline-[rgba(0,0,0,0.08)] -outline-offset-2 border-1 border-white shadow-md',
                },
              }}
            />
          ))}
        </div>
        <DialogTrigger>
          <Button variant="plain">
            <Text>Invite</Text>
          </Button>
          <InviteMembersModal defaultRole={ManagedRole.Reviewer} />
        </DialogTrigger>
      </div>
      <div ref={widthRef} className="gap-sm flex min-h-0 w-full grow flex-col bg-gray-50 px-24 pt-10">
        <InboxProvider agentId={agent?.agentId}>
          <Heading size="xl" className="text-gray-900">
            Runs
          </Heading>
          <RunsActionBar agentId={agent.agentId} />
          {isModelConfigured ? (
            <>
              <SetupAutomaticExportBanner agentId={agent.agentId} />
              <SampleDocumentTooltip className="translate-x-9" offset={96} agentId={agent.agentId} />
              <AgentRunsTable agentId={agent.agentId} columns={columns} />
              <InboxSelectedToolbar agentId={agent.agentId} />
            </>
          ) : (
            <>{modelIsFetched && <ConfigureModelCTA agentId={agent?.agentId} />}</>
          )}
        </InboxProvider>
      </div>
    </div>
  );
};

export const Inbox = memo(IInbox) as typeof IInbox;
